import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';

import { DIALOG_DATA, DialogRef } from '@hglobal/common';

import { TabsComponent } from '@pp/common';
import { AuthStore } from '@pp/core/auth-store';
import { Account, DialogData, getTabsByKeys, Tab, Team } from '@pp/core/entities';
import { PermissionsStore } from '@pp/core/store';

import { DefaultAddressComponent } from './default-address/default-address.component';
import { PersonalInfoComponent } from './personal-info/personal-info.component';

@Component({
  selector: 'pp-edit-user-profile',
  imports: [CommonModule, PersonalInfoComponent, TabsComponent, DefaultAddressComponent],
  templateUrl: './edit-user-profile.component.html',
  styleUrl: './edit-user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditUserProfileComponent {
  readonly #dialog = inject<DialogData<EditUserProfileComponent, { user: Account; org: Team }>>(DIALOG_DATA);
  readonly #dialogRef = inject(DialogRef);
  readonly #authStore = inject(AuthStore);
  readonly #permissions = inject(PermissionsStore);

  readonly #tabsNames = ['info', 'address'];
  readonly tabs = signal(getTabsByKeys(['editUser.tabs.info', 'editUser.tabs.address']));
  tab = 'info';

  get readonlyRights(): boolean {
    return this.#permissions.readonlyRights();
  }

  get dialogData(): { user: Account; org: Team } {
    return this.#dialog.data;
  }

  setTab(tabIndex: number): void {
    this.tab = this.#tabsNames[tabIndex];
  }

  closeDialog(hasUpdates: boolean): void {
    this.#dialogRef.close();

    if (hasUpdates) {
      this.#authStore.whoAmI();
    }
  }

  handleUnsavedChanges(unsaved: boolean, tab: 'info' | 'address'): void {
    const tabIndex = this.#tabsNames.findIndex(tabName => tabName === tab);
    this.tabs.set(unsaved ? this.disableInactiveTab(tabIndex) : this.enableTabs());
  }

  disableInactiveTab(activeTabIndex: number): Tab[] {
    return this.tabs().map(tab =>
      tab.index === activeTabIndex ? tab : { ...tab, disabled: true, tooltip: 'editUser.tabs.unsavedFormText' },
    );
  }

  enableTabs(): Tab[] {
    return this.tabs().map(tab => ({ ...tab, disabled: false, tooltip: null }));
  }
}
